/**
 * <products-slider>
 * One shared index drives two synchronised, infinitely-looping sliders:
 *   - the MEDIA track slides horizontally (translateX sweep). Seamless looping
 *     in both directions is achieved by cloning the last slide before the first
 *     and the first slide after the last, then silently jumping back onto a real
 *     slide once a transition lands on a clone.
 *   - the PRODUCT slides cross-fade (opacity) — they are stacked on top of each
 *     other, only the active one is opaque.
 * Prev/next arrows advance both forever (infinite loop). No external deps.
 */
if (!customElements.get('products-slider')) {
  class ProductsSlider extends HTMLElement {
    static SELECTORS = {
      mediaTrack: '[data-slider-media-track]',
      mediaSlide: '[data-slider-media-slide]',
      productSlide: '[data-slider-product-slide]',
      prev: '[data-slider-prev]',
      next: '[data-slider-next]'
    };

    static ACTIVE_CLASS = 'is-active';

    connectedCallback () {
      this._abort = new AbortController();

      this._mediaTrack = this.querySelector(ProductsSlider.SELECTORS.mediaTrack);
      this._productSlides = Array.from(this.querySelectorAll(ProductsSlider.SELECTORS.productSlide));
      this._prevButton = this.querySelector(ProductsSlider.SELECTORS.prev);
      this._nextButton = this.querySelector(ProductsSlider.SELECTORS.next);

      this._slides = this._mediaTrack
        ? Array.from(this._mediaTrack.querySelectorAll(ProductsSlider.SELECTORS.mediaSlide))
        : [];
      this._count = this._slides.length;
      this._index = 0;
      this._animating = false;

      // Always reveal the first product slide (also covers the single-slide case).
      this._renderProducts();

      // Nothing to loop with a single (or no) slide.
      if (this._count <= 1) return;

      // Track layout: [clone of last] [0] [1] … [n-1] [clone of first].
      // Real slides occupy positions 1…count, so we start on position 1.
      this._buildClones();
      this._mediaPosition = 1;
      this._setMediaTransform(false);

      const { signal } = this._abort;
      this._prevButton?.addEventListener('click', () => this._prev(), { signal });
      this._nextButton?.addEventListener('click', () => this._next(), { signal });
      this._mediaTrack.addEventListener('transitionend', (event) => this._onTransitionEnd(event), { signal });
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    // Bracket the track with edge clones so it can scroll one step past either
    // end before looping silently back onto the matching real slide.
    _buildClones () {
      const leading = this._cloneSlide(this._slides[this._count - 1]);
      const trailing = this._cloneSlide(this._slides[0]);

      this._mediaTrack.insertBefore(leading, this._slides[0]);
      this._mediaTrack.appendChild(trailing);
    }

    _cloneSlide (slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      Object.keys(clone.dataset)
        .filter((key) => key.startsWith('shopify'))
        .forEach((key) => delete clone.dataset[key]);

      return clone;
    }

    _next () {
      if (this._animating) return;
      this._animating = true;

      this._mediaPosition += 1;
      this._index = (this._index + 1) % this._count;

      this._renderProducts();
      this._setMediaTransform(true);
    }

    _prev () {
      if (this._animating) return;
      this._animating = true;

      this._mediaPosition -= 1;
      this._index = (this._index - 1 + this._count) % this._count;

      this._renderProducts();
      this._setMediaTransform(true);
    }

    _onTransitionEnd (event) {
      if (event.target !== this._mediaTrack || event.propertyName !== 'transform') return;
      this._animating = false;

      // Landed on the trailing clone (= first slide): jump to the real first.
      if (this._mediaPosition === this._count + 1) {
        this._mediaPosition = 1;
        this._setMediaTransform(false);
      // Landed on the leading clone (= last slide): jump to the real last.
      } else if (this._mediaPosition === 0) {
        this._mediaPosition = this._count;
        this._setMediaTransform(false);
      }
    }

    _setMediaTransform (animate) {
      if (!animate) this._mediaTrack.style.transition = 'none';

      this._mediaTrack.style.transform = `translate3d(-${this._mediaPosition * 100}%, 0, 0)`;

      if (!animate) {
        void this._mediaTrack.offsetWidth; // force reflow so the jump is not animated
        this._mediaTrack.style.transition = '';
      }
    }

    _renderProducts () {
      this._productSlides.forEach((slide, i) => {
        slide.classList.toggle(ProductsSlider.ACTIVE_CLASS, i === this._index);
      });
    }
  }

  customElements.define('products-slider', ProductsSlider);
}
